/**
 * Planner-scoped API helpers.
 *
 * Small typed layer over the shared `api` client so components don't inline
 * strings all over the place. Every write endpoint funnels through here so
 * an auth failure (403 "confirm your email") lands in one place.
 */
import { api } from "@/lib/api";

// ---- Events / Tasks / Reminders ----
export const plannerAPI = {
  today: (tzOffsetMinutes = new Date().getTimezoneOffset() * -1) =>
    api.get(`/planner/today?tz_offset_minutes=${tzOffsetMinutes}`).then((r) => r.data),
  week: (startYMD) => api.get(`/planner/week?start=${startYMD}`).then((r) => r.data),

  createEvent: (body) => api.post(`/planner/events`, body).then((r) => r.data),
  updateEvent: (id, body) => api.patch(`/planner/events/${id}`, body).then((r) => r.data),
  deleteEvent: (id) => api.delete(`/planner/events/${id}`).then((r) => r.data),
  eventIcsUrl: (id) => `${api.defaults.baseURL}/planner/events/${id}/ics`,

  meals: (weekYMD) => api.get(`/planner/meals?week=${weekYMD}`).then((r) => r.data),
  addMeal: (body) => api.post(`/planner/meals`, body).then((r) => r.data),
  swapMeals: (date_a, date_b, meal_type = "dinner") =>
    api.post(`/planner/meals/swap`, { date_a, date_b, meal_type }).then((r) => r.data),
  mealHistory: () => api.get(`/planner/meals/history`).then((r) => r.data),
  copyMealWeek: (source_week, target_week) =>
    api.post(`/planner/meals/copy-week`, { source_week, target_week }).then((r) => r.data),
  deleteMeal: (id) => api.delete(`/planner/meals/${id}`).then((r) => r.data),
  eligibleRecipes: () => api.get(`/planner/eligible-recipes`).then((r) => r.data),

  shopping: (weekYMD) => api.get(`/planner/shopping?week=${weekYMD}`).then((r) => r.data),
  addShopping: (body) => api.post(`/planner/shopping`, body).then((r) => r.data),
  updateShopping: (id, body) => api.patch(`/planner/shopping/${id}`, body).then((r) => r.data),
  deleteShopping: (id) => api.delete(`/planner/shopping/${id}`).then((r) => r.data),

  share: () => api.get(`/planner/share`).then((r) => r.data),
  rotateShare: () => api.post(`/planner/share`).then((r) => r.data),
  revokeShare: () => api.delete(`/planner/share`).then((r) => r.data),
  publicShare: (token) => api.get(`/planner/shared/${token}`).then((r) => r.data),
};

// ---- Date helpers (client-local; the API is UTC-anchored) ----
export function sundayOf(dateLike) {
  const d = dateLike ? new Date(dateLike) : new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - d.getDay()); // Sunday = 0
  return d;
}
export function ymd(dateLike) {
  const d = new Date(dateLike);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
export function addDays(dateLike, n) {
  const d = new Date(dateLike);
  d.setDate(d.getDate() + n);
  return d;
}
export function formatTime(iso) {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}
export function formatDayLabel(dateLike) {
  const d = new Date(dateLike);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

// ---- Media helpers ----
// Recipe/idea photos are served by GET /api/files/{full storage path}.
// `path` from the API is the FULL storage path (e.g. "willow/uploads/<user>/<hex>.jpg");
// legacy seeds may carry a leading slash which would produce a "//" URL — strip it.
// Returns null when we can't build a usable URL so callers can fall back to a placeholder.
export function recipePhotoUrl(path) {
  if (!path || typeof path !== "string") return null;
  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  if (!backend) return null;
  const clean = path.replace(/^\/+/, "");
  return `${backend}/api/files/${clean}`;
}
